import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Images, Plus, Camera, Share2, Globe2, Lock, Church, Trash2, ExternalLink } from "lucide-react";

export default function Albums() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    church_id: user?.church_id || "",
    is_public: true,
  });

  const canCreate = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","GEMBALA_JEMAAT","SEKRETARIS_JEMAAT"].includes(user?.role);
  const isKlasisAdmin = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get("/albums"),
        churches.length ? Promise.resolve({ data: churches }) : api.get("/churches"),
      ]);
      setItems(a.data);
      if (!churches.length) setChurches(c.data.filter(x => x.type === "JEMAAT"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Nama album wajib"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        church_id: form.church_id || null,
        is_public: !!form.is_public,
        kegiatan_ids: [],
      };
      await api.post("/albums", payload);
      toast.success("Album dibuat. Tambahkan kegiatan foto ke dalamnya.");
      setOpenNew(false);
      setForm({ name: "", description: "", church_id: user?.church_id || "", is_public: true });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Hapus album "${a.name}"? Kegiatan tidak ikut terhapus.`)) return;
    try {
      await api.delete(`/albums/${a.id}`);
      toast.success("Album dihapus");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const share = (a) => {
    const url = `${window.location.origin}/public/album/${a.slug}`;
    navigator.clipboard?.writeText(url);
    toast.success("Link publik disalin ke clipboard");
  };

  const churchName = (id) => churches.find(c => c.id === id)?.short_name || "—";

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Pelayanan • Galeri</div>
          <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
            <Images className="h-7 w-7 text-secondary" /> Album Kegiatan
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Kumpulan foto kegiatan yang bisa dibagikan ke jemaat melalui link publik unik.
          </p>
        </div>
        {canCreate && (
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-new-album"><Plus className="h-4 w-4 mr-2" /> Album Baru</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Album Baru</DialogTitle>
                <DialogDescription>Buat album lalu tambahkan kegiatan foto ke dalamnya. Album publik dapat dibagikan lewat link.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Nama Album</Label>
                  <Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} placeholder="Contoh: Natal Bersama 2026" data-testid="input-album-name" />
                </div>
                <div>
                  <Label>Deskripsi (opsional)</Label>
                  <Textarea rows={2} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} placeholder="Ringkasan album…" data-testid="input-album-desc" />
                </div>
                {isKlasisAdmin && (
                  <div>
                    <Label>Cakupan</Label>
                    <Select value={form.church_id || "KLASIS"} onValueChange={(v)=>setForm({...form, church_id: v === "KLASIS" ? null : v})}>
                      <SelectTrigger data-testid="select-album-scope"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="KLASIS">Tingkat Klasis</SelectItem>
                        {churches.map(c => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center justify-between border border-border rounded-md p-3">
                  <div>
                    <div className="text-sm font-medium">Album Publik</div>
                    <div className="text-xs text-muted-foreground">Link dapat dibagikan tanpa login</div>
                  </div>
                  <Switch checked={!!form.is_public} onCheckedChange={(v)=>setForm({...form, is_public:v})} data-testid="switch-album-public" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpenNew(false)}>Batal</Button>
                <Button onClick={create} disabled={saving} data-testid="btn-save-album">{saving ? "Menyimpan…" : "Simpan Album"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Memuat album…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="albums-empty">
          <Images className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Belum ada album. Klik <strong>Album Baru</strong> untuk mulai.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="albums-grid">
          {items.map((a) => (
            <Card key={a.id} className="overflow-hidden hover:border-primary/30 transition-colors group" data-testid={`album-${a.id}`}>
              <Link to={`/album/${a.id}`} className="block">
                <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                  {a.cover_thumb ? (
                    <img src={a.cover_thumb} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                      <Camera className="h-10 w-10 mb-1" />
                      <div className="text-xs">Belum ada foto</div>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex items-center gap-1">
                    {a.is_public ? (
                      <div className="bg-emerald-600 text-white text-[0.65rem] px-2 py-0.5 rounded-full flex items-center gap-1"><Globe2 className="h-3 w-3" /> Publik</div>
                    ) : (
                      <div className="bg-muted-foreground text-white text-[0.65rem] px-2 py-0.5 rounded-full flex items-center gap-1"><Lock className="h-3 w-3" /> Internal</div>
                    )}
                  </div>
                </div>
              </Link>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/album/${a.id}`} className="min-w-0">
                    <div className="font-heading font-bold text-sm truncate hover:text-primary transition-colors">{a.name}</div>
                    <div className="text-[0.7rem] text-muted-foreground mt-0.5 flex items-center gap-2">
                      {a.church_id && <span className="inline-flex items-center gap-1"><Church className="h-3 w-3" /> {churchName(a.church_id)}</span>}
                      <span>{a.kegiatan_count || 0} kegiatan • {a.photo_count || 0} foto</span>
                    </div>
                  </Link>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                  {a.is_public && (
                    <button onClick={()=>share(a)} className="text-[0.7rem] text-primary hover:text-secondary transition-colors flex items-center gap-1" data-testid={`btn-share-${a.id}`}>
                      <Share2 className="h-3 w-3" /> Bagikan link
                    </button>
                  )}
                  <a href={`/public/album/${a.slug}`} target="_blank" rel="noreferrer" className="text-[0.7rem] text-muted-foreground hover:text-primary flex items-center gap-1" data-testid={`btn-preview-${a.id}`}>
                    <ExternalLink className="h-3 w-3" /> Preview
                  </a>
                  {canCreate && (
                    <button onClick={()=>del(a)} className="ml-auto text-[0.7rem] text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid={`btn-del-album-${a.id}`}>
                      <Trash2 className="h-3 w-3" /> Hapus
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
